import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ShoppingCart, Heart, User, MagnifyingGlass,
  Phone, Envelope, House,
  FacebookLogo, InstagramLogo, TiktokLogo, List, X, Tag,
  Truck, MapPin
} from '@phosphor-icons/react';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import { sanitizeHTML } from '../../utils/sanitize';
import { MagereHeader as SharedMagereHeader, MagereFooter as SharedMagereFooter } from './MagereSharedLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "magere-viandes";

const CmsPageView = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug, pageSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const [store, setStore] = useState(null);
  const [template, setTemplate] = useState(null);
  const [page, setPage] = useState(null);
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { itemCount, setIsOpen, setStoreSlug } = useCart();

  // Helper for standalone links
  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;
  
  const isMagere = store?.identity?.theme_template === 'magere' || storeSlug === 'magere-viandes' || standalone;

  const colors = useMemo(() => {
    const tc = template?.colors || {};
    const si = store?.identity || {};
    return {
      primary: tc.primary || si.primary_color || '#1a3d5c',
      secondary: tc.secondary || si.secondary_color || '#0cb5c6',
      accent: tc.accent || si.accent_color || '#e0a800',
    };
  }, [template, store]);

  const logoUrl = store?.identity?.logo_light || store?.theme?.assets?.logo_url || '';
  const sid = store?.identity || {};
  const sco = store?.company || {};
  const phone = sco.contact_phone || sid.contact_phone || '';
  const email = sco.contact_email || sid.contact_email || '';
  const companyName = sco.company_name || sid.company_name || store?.name || '';
  const fbUrl = sco.social?.facebook || sid.social_facebook || '';
  const igUrl = sco.social?.instagram || sid.social_instagram || '';
  const ttUrl = sco.social?.tiktok || sid.social_tiktok || '';
  const companyAddress = [sco.address_line, [sco.postal_code, sco.city].filter(Boolean).join(' ')].filter(s => s && s.trim()).join(', ');
  const address = companyAddress || sid.contact_address || '';
  const [deliveryInfo, setDeliveryInfo] = useState(null);

  useEffect(() => { if (storeSlug) setStoreSlug(storeSlug); }, [storeSlug, setStoreSlug]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const [s, t, c, p] = await Promise.all([
          axios.get(`${API}/api/s/${storeSlug}`),
          axios.get(`${API}/api/s/${storeSlug}/template`),
          axios.get(`${API}/api/s/${storeSlug}/categories`),
          axios.get(`${API}/api/s/${storeSlug}/pages`),
        ]);
        setStore(s.data);
        setTemplate(t.data);
        setCategories(c.data || []);
        setPages(p.data || []);
        // Fetch delivery info
        try {
          const di = await axios.get(`${API}/api/s/${storeSlug}/delivery-info`);
          setDeliveryInfo(di.data);
        } catch (e) { console.error('Delivery info fetch error:', e); }
        if (pageSlug) {
          try {
            const pg = await axios.get(`${API}/api/s/${storeSlug}/pages/${pageSlug}`);
            setPage(pg.data);
          } catch (e) { console.error('CMS page fetch error:', e); setNotFound(true); }
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [storeSlug, pageSlug]);

  /* ═══════════ LE MAGASIN HOOKS (must be before any return) ═══════════ */
  const pd = page?.page_data || {};
  const isLeMagasin = pageSlug === 'le-magasin' && isMagere;
  const heroImages = pd.hero_images || [];
  const [heroIdx, setHeroIdx] = React.useState(0);
  const [visibleEntries, setVisibleEntries] = React.useState(new Set());
  const timelineRef = React.useRef(null);

  /* ═══════════ CONTACT HOOKS (must be before any return) ═══════════ */
  const [contactForm, setContactForm] = React.useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [contactSending, setContactSending] = React.useState(false);
  const [contactSent, setContactSent] = React.useState(false);

  const heroHovered = React.useRef(false);

  React.useEffect(() => {
    if (!isLeMagasin || heroImages.length <= 1) return;
    const t = setInterval(() => {
      if (!heroHovered.current) setHeroIdx(prev => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(t);
  }, [isLeMagasin, heroImages.length]);

  React.useEffect(() => {
    if (!isLeMagasin || !timelineRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) setVisibleEntries(prev => new Set([...prev, e.target.dataset.idx]));
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    const items = timelineRef.current?.querySelectorAll('[data-idx]');
    items?.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [isLeMagasin, page]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600" />
    </div>
  );

  /* ═══════════ TPS HEADER ═══════════ */
  const TpsHeader = () => (
    <header className="sticky top-0 z-50" data-testid="content-header">
      <div className="bg-white">
        <div className="max-w-[1300px] mx-auto px-6 flex items-center justify-between" style={{ height: 90 }}>
          <Link to={getLink('/')} className="flex-shrink-0">
            {logoUrl ? <img src={logoUrl} alt={store?.name} className="h-[65px] max-w-[260px] object-contain" />
             : <span className="text-2xl font-extrabold" style={{ color: colors.primary }}>{store?.name}</span>}
          </Link>
          <div className="flex items-center gap-5">
            <Link to={getLink('/login')} className="hidden md:flex flex-col items-center gap-0.5 text-slate-600 hover:text-slate-800">
              <User size={26} weight="thin" /><span className="text-[10px]">Compte</span>
            </Link>
            <button className="relative flex flex-col items-center gap-0.5 text-slate-600" onClick={() => setIsOpen(true)} data-testid="cart-btn">
              <ShoppingCart size={26} weight="thin" /><span className="text-[10px]">Panier</span>
              <span className="absolute -top-1.5 -right-2 w-[16px] h-[16px] text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ backgroundColor: colors.secondary }}>{itemCount}</span>
            </button>
          </div>
        </div>
      </div>
      <nav style={{ backgroundColor: colors.primary }}>
        <div className="max-w-[1300px] mx-auto px-6 flex items-center h-[44px]">
          <Link to={getLink('/')} className="px-4 h-[44px] flex items-center text-[13px] font-medium text-white hover:bg-white/10">Accueil</Link>
          <span className="text-white/30 text-[14px]">|</span>
          {categories.slice(0, 7).map((c, i) => (
            <React.Fragment key={c.id}>
              <Link to={getLink('/')} className="px-4 h-[44px] flex items-center text-[13px] font-medium text-white hover:bg-white/10">{c.name}</Link>
              {i < Math.min(categories.length, 7) - 1 && <span className="text-white/30 text-[14px]">|</span>}
            </React.Fragment>
          ))}
        </div>
      </nav>
    </header>
  );

  /* ═══════════ TPS FOOTER ═══════════ */
  const TpsFooter = () => (
    <footer style={{ backgroundColor: colors.primary }} data-testid="content-footer">
      <div className="max-w-[1300px] mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            {logoUrl ? <img src={logoUrl} alt={store?.name} className="h-[70px] mb-4 object-contain brightness-0 invert" />
             : <span className="text-lg font-extrabold text-white block mb-4">{store?.name}</span>}
          </div>
          <div>
            <h4 className="text-[13px] font-bold mb-4 text-white">Categories</h4>
            <ul className="space-y-2">{categories.slice(0, 5).map(c => (
              <li key={c.id}><Link to={getLink('/')} className="text-[12px] text-white/60 hover:text-white">{c.name}</Link></li>
            ))}</ul>
          </div>
          <div>
            <h4 className="text-[13px] font-bold mb-4" style={{ color: colors.secondary }}>Informations</h4>
            <ul className="space-y-2">{pages.filter(p => p.is_published).map(p => (
              <li key={p.id}><Link to={getLink(`/page/${p.slug}`)} className={`text-[12px] text-white/60 hover:text-white ${p.slug === pageSlug ? 'text-white font-bold' : ''}`}>{p.title}</Link></li>
            ))}</ul>
          </div>
          <div>
            <h4 className="text-[13px] font-bold mb-2" style={{ color: colors.secondary }}>Service clients</h4>
            <p className="text-[18px] font-extrabold text-white">07 73 79 19 96</p>
          </div>
        </div>
        <div className="border-t border-white/10 mt-10 pt-5 text-center">
          <span className="text-[11px] text-white/50">&copy; {new Date().getFullYear()} {store?.name}</span>
        </div>
      </div>
    </footer>
  );

  const activePage = pageSlug === 'le-magasin' ? 'le-magasin' : pageSlug === 'contact' ? 'contact' : '';
  const HeaderComp = isMagere ? () => <SharedMagereHeader store={store} activePage={activePage} standalone={standalone} /> : TpsHeader;
  const FooterComp = isMagere ? () => <SharedMagereFooter store={store} pages={pages} categories={categories} standalone={standalone} /> : TpsFooter;
  const fontFamily = isMagere ? "'Jost', sans-serif" : "'Inter', 'Segoe UI', sans-serif";
  const pageContent = page?.content_html || page?.content || '';

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" style={{ fontFamily }}>
        <HeaderComp />
        <div className="h-[50px]" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>Page introuvable</h1>
            <Link to={getLink('/')} className="text-sm underline" style={{ color: colors.secondary }}>Retour à la boutique</Link>
          </div>
        </main>
        <FooterComp />
      </div>
    );
  }

  /* ═══════════ CONTACT PAGE — Custom Template ═══════════ */
  const isContact = pageSlug === 'contact' && isMagere;

  // Horaires dynamiques depuis Réglages → Entreprise (tous les jours affichés, lundi → dimanche)
  const buildDailyHours = (oh) => {
    const DAYS_ORDER = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
    const fmt = (t) => (t || '').replace(':', 'h');
    const rows = DAYS_ORDER.map(d => {
      const h = (oh || {})[d] || {};
      let s = '';
      if (h.closed) s = 'Fermé';
      else if (h.open && h.close) {
        s = `${fmt(h.open)} - ${fmt(h.close)}`;
        if (h.open2 && h.close2) s += ` / ${fmt(h.open2)} - ${fmt(h.close2)}`;
      }
      return { day: d.charAt(0).toUpperCase() + d.slice(1), hours: s || 'Fermé', _days: [d], _has: !!s || h.closed };
    });
    return rows.some(r => r._has) ? rows : null;
  };
  const companyHours = buildDailyHours(store?.company?.opening_hours);

  const storeHours = companyHours || pd.hours || [
    { day: 'Mardi - Vendredi', hours: '8h00 - 12h30 / 15h30 - 19h00' },
    { day: 'Samedi', hours: '8h00 - 12h30 / 15h00 - 19h00' },
    { day: 'Dimanche', hours: '8h00 - 12h30' },
    { day: 'Lundi', hours: 'Fermé' },
  ];

  const getCurrentDayIndex = () => {
    const todayName = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][new Date().getDay()];
    const byDays = storeHours.findIndex(r => Array.isArray(r._days) && r._days.includes(todayName));
    if (byDays !== -1) return byDays;
    const day = new Date().getDay(); // 0=Sun, 1=Mon, ...
    // Fallback pour le format libre par défaut : 0=Mar-Ven, 1=Sam, 2=Dim, 3=Lun
    if (day >= 2 && day <= 5) return 0;
    if (day === 6) return 1;
    if (day === 0) return 2;
    return 3;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      await fetch(`${API}/api/s/${storeSlug}/contact`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      setContactSent(true);
    } catch (err) { /* ignore */ }
    setContactSending(false);
  };

  if (isContact) {
    const todayIdx = getCurrentDayIndex();
    return (
      <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" data-testid="contact-page" style={{ fontFamily }}>
        <HeaderComp />
        <div className="h-[50px]" />
        <main className="flex-1">

          {/* Hero */}
          <div className="relative py-14 md:py-20" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="max-w-[1100px] mx-auto px-6 text-center">
              <span className="text-xs font-bold uppercase tracking-[0.25em] mb-3 block" style={{ color: colors.primary }}>Contact</span>
              <h1 className="text-3xl md:text-5xl font-black text-white mb-3" style={{ letterSpacing: '-0.02em' }}>Nous contacter</h1>
              <p className="text-white/60 text-base max-w-[500px] mx-auto">{pd.intro_text || "Une question, une commande spéciale, un devis professionnel ? Notre équipe est à votre écoute."}</p>
            </div>
          </div>

          {/* Info Cards */}
          <section className="py-12 md:py-16" style={{ backgroundColor: '#faf9f7' }}>
            <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Phone */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow" data-testid="contact-phone-card">
                <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colors.primary + '15' }}>
                  <Phone size={24} weight="fill" style={{ color: colors.primary }} />
                </div>
                <h3 className="text-lg font-bold mb-1" style={{ color: '#1a1a1a' }}>Téléphone</h3>
                <a href={`tel:${(phone || '04 90 17 42 67').replace(/\s/g, '')}`} className="text-lg font-bold hover:underline" style={{ color: colors.primary }}>{phone || '04 90 17 42 67'}</a>
                <p className="text-[13px] text-slate-500 mt-1">{pd.phone_note || 'Du mardi au samedi'}</p>
              </div>
              {/* Email */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow" data-testid="contact-email-card">
                <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colors.primary + '15' }}>
                  <Envelope size={24} weight="fill" style={{ color: colors.primary }} />
                </div>
                <h3 className="text-lg font-bold mb-1" style={{ color: '#1a1a1a' }}>Email</h3>
                <a href={`mailto:${email || 'jeremy.magere@gmail.com'}`} className="text-[15px] font-semibold hover:underline" style={{ color: colors.primary }}>{email || 'jeremy.magere@gmail.com'}</a>
                <p className="text-[13px] text-slate-500 mt-1">{pd.email_note || 'Réponse sous 24h'}</p>
              </div>
              {/* Parking */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center hover:shadow-md transition-shadow" data-testid="contact-parking-card">
                <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#22c55e20' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 256 256" fill="#22c55e"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-4-152H104a8,8,0,0,0-8,8v112a8,8,0,0,0,16,0V152h12a44,44,0,0,0,0-88Zm0,72H112V80h12a28,28,0,0,1,0,56Z" /></svg>
                </div>
                <h3 className="text-lg font-bold mb-1" style={{ color: '#1a1a1a' }}>{pd.parking_title || 'Grand parking gratuit'}</h3>
                <p className="text-[15px] font-semibold text-green-600">{pd.parking_text || 'Accès facile et gratuit'}</p>
                <p className="text-[13px] text-slate-500 mt-1">{pd.parking_note || 'Plus de 50 places disponibles'}</p>
              </div>
            </div>
          </section>

          {/* Hours + Form */}
          <section className="py-12 md:py-16">
            <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">

              {/* Left: Hours + Access */}
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: colors.primary }}>Horaires d'ouverture</span>
                <h2 className="text-2xl font-bold mb-6" style={{ color: '#1a1a1a' }}>Venez nous voir</h2>
                <div className="space-y-0 mb-8" data-testid="hours-list">
                  {storeHours.map((h, i) => {
                    const isToday = i === todayIdx;
                    return (
                      <div key={i} className={`flex items-center justify-between py-3.5 border-b border-slate-100 ${isToday ? 'rounded-lg px-3 -mx-3' : ''}`}
                        style={isToday ? { backgroundColor: colors.primary + '08', borderColor: colors.primary + '20' } : {}}>
                        <div className="flex items-center gap-2">
                          {isToday && <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: colors.primary }} />}
                          <span className={`text-[15px] ${isToday ? 'font-bold' : 'text-slate-600'}`} style={isToday ? { color: colors.primary } : {}}>
                            {h.day} {isToday && <span className="text-[11px] font-normal ml-1">(aujourd'hui)</span>}
                          </span>
                        </div>
                        <span className={`text-[15px] font-semibold ${h.hours === 'Fermé' ? '' : ''}`}
                          style={h.hours === 'Fermé' ? { color: '#ef4444' } : isToday ? { color: colors.primary } : { color: '#1a1a1a' }}>
                          {h.hours}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Access info */}
                <div className="bg-slate-50 rounded-2xl p-6 space-y-4" data-testid="access-info">
                  <h3 className="text-lg font-bold" style={{ color: '#1a1a1a' }}>Accès routier</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin size={18} weight="fill" className="flex-shrink-0 mt-0.5" style={{ color: colors.primary }} />
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{companyName || 'SARL MAGERE — Boucherie Magere & Fils'}</p>
                        <p className="text-[13px] text-slate-500">{address || 'Provence, Vaucluse (84)'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 256 256" fill={colors.primary} className="flex-shrink-0 mt-0.5"><path d="M240,96H224V72a8,8,0,0,0-8-8H40a8,8,0,0,0-8,8V96H16a8,8,0,0,0-8,8v48a8,8,0,0,0,8,8H32v24a8,8,0,0,0,8,8H72a8,8,0,0,0,8-8V160h96v24a8,8,0,0,0,8,8h32a8,8,0,0,0,8-8V160h16a8,8,0,0,0,8-8V104A8,8,0,0,0,240,96Z" /></svg>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{pd.access_title || "Accès depuis l'autoroute"}</p>
                        <p className="text-[13px] text-slate-500">{pd.access_text || 'Sortie autoroute A7, direction centre-ville. Suivre la signalétique "MAGERE Viandes".'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 256 256" fill="#22c55e" className="flex-shrink-0 mt-0.5"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-4-152H104a8,8,0,0,0-8,8v112a8,8,0,0,0,16,0V152h12a44,44,0,0,0,0-88Zm0,72H112V80h12a28,28,0,0,1,0,56Z" /></svg>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">Parking gratuit</p>
                        <p className="text-[13px] text-slate-500">{pd.parking_detail || 'Grand parking gratuit de plus de 50 places juste devant le magasin. Accès facile même en véhicule utilitaire pour les professionnels.'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery info */}
                <div className="mt-6 p-5 rounded-2xl border-2" style={{ borderColor: colors.primary + '30', backgroundColor: colors.primary + '05' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Truck size={18} weight="fill" style={{ color: colors.primary }} />
                    <span className="text-[14px] font-bold" style={{ color: colors.primary }}>{pd.delivery_title || 'Livraison à domicile'}</span>
                  </div>
                  {pd.delivery_text ? (
                    <p className="text-[13px] text-slate-600" style={{ whiteSpace: 'pre-line' }}>{pd.delivery_text}</p>
                  ) : deliveryInfo && (deliveryInfo.zones || []).length > 0 ? (
                    <div className="space-y-1.5">
                      {deliveryInfo.zones.map((z, i) => (
                        <p key={i} className="text-[13px] text-slate-600">
                          <strong>{z.name}</strong> — {(z.delivery_days || []).join(', ')}
                          {z.free_above && <> · offerte dès <strong style={{ color: colors.primary }}>{z.free_above}€</strong></>}
                        </p>
                      ))}
                      <p className="text-[12px] text-slate-500">{pd.delivery_footnote || "La liste des communes desservies s'affiche automatiquement au moment de la commande selon votre code postal."}</p>
                    </div>
                  ) : (
                    <p className="text-[13px] text-slate-600">Contactez-nous pour connaitre nos zones de livraison.</p>
                  )}
                </div>
              </div>

              {/* Right: Contact Form */}
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: colors.primary }}>Formulaire</span>
                <h2 className="text-2xl font-bold mb-6" style={{ color: '#1a1a1a' }}>Envoyez-nous un message</h2>

                {contactSent ? (
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-testid="contact-success">
                    <div className="w-16 h-16 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#22c55e" viewBox="0 0 256 256"><path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z" /></svg>
                    </div>
                    <h3 className="text-xl font-bold text-green-800 mb-2">Message envoyé !</h3>
                    <p className="text-[14px] text-green-700">Merci pour votre message. Nous vous répondrons dans les plus brefs délais.</p>
                    <button onClick={() => { setContactSent(false); setContactForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                      className="mt-4 text-[13px] underline text-green-600">Envoyer un autre message</button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[12px] font-semibold text-slate-600 block mb-1">Nom complet *</label>
                        <input type="text" required value={contactForm.name} onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-offset-1"
                          style={{ '--tw-ring-color': colors.primary }} placeholder="Votre nom" data-testid="contact-name" />
                      </div>
                      <div>
                        <label className="text-[12px] font-semibold text-slate-600 block mb-1">Email *</label>
                        <input type="email" required value={contactForm.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-offset-1"
                          placeholder="votre@email.com" data-testid="contact-email" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[12px] font-semibold text-slate-600 block mb-1">Téléphone</label>
                        <input type="tel" value={contactForm.phone} onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-offset-1"
                          placeholder="06 12 34 56 78" data-testid="contact-phone" />
                      </div>
                      <div>
                        <label className="text-[12px] font-semibold text-slate-600 block mb-1">Sujet</label>
                        <select value={contactForm.subject} onChange={e => setContactForm(p => ({ ...p, subject: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-offset-1 bg-white"
                          data-testid="contact-subject">
                          <option value="">Choisir un sujet</option>
                          <option value="commande">Commande en ligne</option>
                          <option value="devis-pro">Devis professionnel</option>
                          <option value="evenement">Événement / Traiteur</option>
                          <option value="livraison">Question livraison</option>
                          <option value="reclamation">Réclamation</option>
                          <option value="autre">Autre</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-[12px] font-semibold text-slate-600 block mb-1">Message *</label>
                      <textarea required rows={5} value={contactForm.message} onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-offset-1 resize-none"
                        placeholder="Votre message..." data-testid="contact-message" />
                    </div>
                    <button type="submit" disabled={contactSending}
                      className="w-full py-3.5 rounded-xl text-white font-bold text-[15px] transition-all hover:brightness-110 disabled:opacity-50"
                      style={{ backgroundColor: colors.primary }} data-testid="contact-submit">
                      {contactSending ? 'Envoi en cours...' : 'Envoyer le message'}
                    </button>
                    <p className="text-[11px] text-slate-400 text-center">Nous vous répondrons dans les 24 heures. Vos données sont protégées.</p>
                  </form>
                )}
              </div>
            </div>
          </section>

          {/* Pro section */}
          <section className="py-12 md:py-16" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="max-w-[900px] mx-auto px-6 text-center">
              <span className="text-xs font-bold uppercase tracking-[0.25em] mb-3 block" style={{ color: colors.primary }}>Professionnels</span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{pd.pro_title || 'Vous êtes un professionnel ?'}</h2>
              <p className="text-white/60 text-[15px] leading-relaxed mb-6 max-w-[600px] mx-auto">
                {pd.pro_text || "Restaurateurs, traiteurs, collectivités : bénéficiez de nos tarifs professionnels, de la livraison prioritaire et d'un interlocuteur dédié pour vos commandes en gros et demi-gros."}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href={`tel:${(phone || '04 90 17 42 67').replace(/\s/g, '')}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold hover:brightness-110 transition-all"
                  style={{ backgroundColor: colors.primary }}>
                  <Phone size={18} weight="fill" /> Appelez-nous
                </a>
                <a href={`mailto:${email || 'jeremy.magere@gmail.com'}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold border-2 text-white hover:bg-white/10 transition-all"
                  style={{ borderColor: colors.primary, color: colors.primary }}>
                  <Envelope size={18} weight="fill" /> Demander un devis
                </a>
              </div>
            </div>
          </section>

        </main>
        <FooterComp />
      </div>
    );
  }

  /* ═══════════ LE MAGASIN — Rich Template ═══════════ */
  if (isLeMagasin) {
    const timeline = pd.timeline || [];
    const values = pd.values || [];
    const hours = companyHours || pd.hours || [];

    return (
      <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" data-testid="cms-page-view" style={{ fontFamily }}>
        <HeaderComp />
        <div className="h-[50px]" />
        <main className="flex-1">

          {/* ── HERO — Video or Image Carousel ── */}
          {heroImages.length > 0 ? (
          <div className="relative w-full overflow-hidden" style={{ height: 'clamp(280px, 45vw, 480px)' }} data-testid="hero-carousel"
            onMouseEnter={() => { heroHovered.current = true; }} onMouseLeave={() => { heroHovered.current = false; }}>
            {/* Overlay gradient */}
            <div className="absolute inset-0 z-[2]" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.3) 100%)' }} />

            {/* Sliding images track */}
            <div className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ width: `${heroImages.length * 50}%`, transform: `translateX(-${heroIdx * (100 / heroImages.length)}%)` }}>
              {heroImages.map((img, i) => (
                <div key={i} className="h-full flex-shrink-0" style={{ width: `${100 / heroImages.length}%` }}>
                  <img src={img.url} alt={img.alt || ''} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>

            {/* Text overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 z-10">
              <div className="max-w-[1100px] mx-auto">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-2 drop-shadow-lg" style={{ letterSpacing: '-0.02em' }}>
                  {page?.title || 'Le magasin'}
                </h1>
                {(pd.hero_subtitle || heroImages[heroIdx]?.caption) && (
                  <p className="text-white/80 text-base md:text-lg font-light max-w-[600px]">{pd.hero_subtitle || heroImages[heroIdx].caption}</p>
                )}
              </div>
            </div>
            {/* Nav arrows */}
            {heroImages.length > 2 && (
              <>
                <button onClick={() => setHeroIdx(prev => prev === 0 ? Math.max(0, heroImages.length - 2) : prev - 1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors"
                  data-testid="hero-prev">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" fill="currentColor"><path d="M168.49,199.51a12,12,0,0,1-17,17l-80-80a12,12,0,0,1,0-17l80-80a12,12,0,0,1,17,17L97,128Z"/></svg>
                </button>
                <button onClick={() => setHeroIdx(prev => prev >= heroImages.length - 2 ? 0 : prev + 1)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors"
                  data-testid="hero-next">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" fill="currentColor"><path d="M184.49,136.49l-80,80a12,12,0,0,1-17-17L159,128,87.51,56.49a12,12,0,0,1,17-17l80,80A12,12,0,0,1,184.49,136.49Z"/></svg>
                </button>
              </>
            )}
            {/* Dots */}
            {heroImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {heroImages.map((_, i) => (
                  <button key={i} onClick={() => setHeroIdx(i)}
                    className="w-2.5 h-2.5 rounded-full transition-all duration-300"
                    style={{ backgroundColor: i === heroIdx ? colors.primary : 'rgba(255,255,255,0.4)', transform: i === heroIdx ? 'scale(1.3)' : 'scale(1)' }}
                    data-testid={`hero-dot-${i}`} />
                ))}
              </div>
            )}
          </div>
          ) : (
            /* Simple hero with title when no hero_images */
            <div className="relative py-14 md:py-20" style={{ backgroundColor: '#1a1a1a' }} data-testid="hero-simple">
              <div className="max-w-[1100px] mx-auto px-6 text-center">
                <span className="text-xs font-bold uppercase tracking-[0.25em] mb-3 block" style={{ color: colors.primary }}>Découvrez-nous</span>
                <h1 className="text-3xl md:text-5xl font-black text-white mb-3" style={{ letterSpacing: '-0.02em' }}>
                  {page?.title || 'Le magasin'}
                </h1>
                <p className="text-white/60 text-base max-w-[500px] mx-auto">{pd.hero_subtitle || "Une passion familiale pour la viande d'exception depuis 1985"}</p>
              </div>
            </div>
          )}

          {/* ── INTRO SECTION ── */}
          <section className="py-14 md:py-20" style={{ backgroundColor: '#faf9f7' }}>
            <div className="max-w-[800px] mx-auto px-6 text-center">
              <div className="w-16 h-[3px] mx-auto mb-6 rounded-full" style={{ backgroundColor: colors.primary }} />
              <h2 className="text-2xl md:text-3xl font-bold mb-5" style={{ color: '#1a1a1a' }}>{pd.story_title || 'Notre histoire'}</h2>
              {pd.story_text ? (
                <p className="text-base md:text-lg text-slate-600 leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{pd.story_text}</p>
              ) : (
                <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                  Depuis plus de <strong className="font-semibold" style={{ color: colors.primary }}>30 ans</strong>, la famille Magere perpétue un savoir-faire artisanal d'exception au cœur du Vaucluse.
                  Fondée par la passion de la viande et le respect du produit, notre boucherie est devenue une référence incontournable pour les amateurs de viandes de qualité supérieure.
                </p>
              )}
            </div>
          </section>

          {/* ── YOUTUBE VIDEO ── */}
          {pd.youtube_video_id && (
            <section className="py-16 md:py-20" data-testid="video-section">
              <div className="max-w-[900px] mx-auto px-6">
                <div className="text-center mb-10">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: colors.primary }}>Découvrez-nous</span>
                  <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1a1a1a' }}>Visite du magasin</h2>
                </div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${pd.youtube_video_id}?rel=0&modestbranding=1`}
                    title="Présentation Magere Viandes"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="youtube-embed"
                  />
                </div>
              </div>
            </section>
          )}

          {/* ── TIMELINE ── */}
          {timeline.length > 0 && (
            <section className="py-16 md:py-24 relative" data-testid="timeline-section" ref={timelineRef}>
              <div className="max-w-[1000px] mx-auto px-6">
                <div className="text-center mb-14">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: colors.primary }}>Notre parcours</span>
                  <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1a1a1a' }}>Plus de 30 ans d'excellence</h2>
                </div>
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[2px] hidden md:block" style={{ backgroundColor: colors.primary + '25' }} />

                  {timeline.map((entry, i) => {
                    const isLeft = i % 2 === 0;
                    const isVisible = visibleEntries.has(String(i));
                    return (
                      <div key={i} data-idx={i}
                        className={`relative mb-12 md:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        {/* Year badge — center on desktop */}
                        <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 top-4 z-10 w-[60px] h-[60px] rounded-full items-center justify-center text-white font-bold text-[13px] shadow-lg"
                          style={{ backgroundColor: colors.primary }} data-testid={`timeline-year-${i}`}>
                          {entry.year}
                        </div>

                        <div className={`md:flex items-start gap-8 ${isLeft ? '' : 'md:flex-row-reverse'}`}>
                          {/* Image side */}
                          <div className={`md:w-[calc(50%-50px)] ${isLeft ? 'md:text-right md:pr-6' : 'md:pl-6'}`}>
                            {entry.image_url && (
                              <div className="rounded-xl overflow-hidden shadow-lg mb-4 md:mb-0 aspect-[4/3]">
                                <img src={entry.image_url} alt={entry.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
                              </div>
                            )}
                          </div>
                          {/* Content side */}
                          <div className={`md:w-[calc(50%-50px)] ${isLeft ? 'md:pl-6' : 'md:pr-6 md:text-right'}`}>
                            <div className="flex items-center gap-3 mb-3 md:hidden">
                              <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[11px] font-bold" style={{ backgroundColor: colors.primary }}>{entry.year}</span>
                              <h3 className="text-lg font-bold" style={{ color: '#1a1a1a' }}>{entry.title}</h3>
                            </div>
                            <h3 className="hidden md:block text-xl font-bold mb-2 mt-4" style={{ color: '#1a1a1a' }}>{entry.title}</h3>
                            <p className="text-[15px] text-slate-600 leading-relaxed">{entry.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* ── VALUES ── */}
          {values.length > 0 && (
            <section className="py-16 md:py-20" style={{ backgroundColor: '#1a1a1a' }} data-testid="values-section">
              <div className="max-w-[1100px] mx-auto px-6">
                <div className="text-center mb-12">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: colors.primary }}>Nos engagements</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">Ce qui nous anime</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                  {values.map((v, i) => (
                    <div key={i} className="text-center p-6 rounded-xl border border-white/10 hover:border-white/20 transition-colors bg-white/[0.03]">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" style={{ backgroundColor: colors.primary + '20' }}>
                        {v.icon === 'leaf' && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256" fill={colors.primary}><path d="M223.45,39.07a8,8,0,0,0-5.52-5.52C209.94,30.89,164.91,22.71,125,47.05,98.18,63.07,81.56,88.63,76.07,121.74,63.43,114,47.61,109.9,31.14,112.69a8,8,0,0,0-6.46,9.22c5.34,31.21,21.79,45.54,34.27,52.27A86,86,0,0,0,56,200a8,8,0,0,0,16,0c0-29.4,15.64-58.44,43-79.87A133.88,133.88,0,0,0,137.09,138c-18.31,25-26.24,51.94-22.64,77.05a8,8,0,0,0,7.92,6.95,7.69,7.69,0,0,0,1.05-.07,8,8,0,0,0,6.88-9c-3.12-21.75,3.89-45.27,20.27-68,10.37-14.37,24-27.26,40.51-38.29C224.4,84.1,228.49,49,223.45,39.07Z"/></svg>}
                        {v.icon === 'heart' && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256" fill={colors.primary}><path d="M240,98a57.63,57.63,0,0,1-17,41L128,234,33,139A58,58,0,0,1,114.77,57.34a6,6,0,0,1,.46.46L128,72.14l12.77-14.34a6,6,0,0,1,.46-.46A58,58,0,0,1,240,98Z"/></svg>}
                        {v.icon === 'knife' && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256" fill={colors.primary}><path d="M231.87,32.13a28,28,0,0,0-39.6,0L18.34,206.06a8,8,0,0,0,0,11.31l20.29,20.29a8,8,0,0,0,11.31,0L231.87,55.73A28,28,0,0,0,231.87,32.13Z"/></svg>}
                        {v.icon === 'users' && <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256" fill={colors.primary}><path d="M117.25,157.92a60,60,0,1,0-66.5,0A95.83,95.83,0,0,0,3.53,195.63a8,8,0,1,0,13.4,8.74,80,80,0,0,1,134.14,0,8,8,0,0,0,13.4-8.74A95.83,95.83,0,0,0,117.25,157.92ZM40,108a44,44,0,1,1,44,44A44.05,44.05,0,0,1,40,108Zm210.14,98.7a8,8,0,0,1-11.07-2.33A79.83,79.83,0,0,0,172,168a8,8,0,0,1,0-16,44,44,0,1,0-16.34-84.87,8,8,0,1,1-5.94-14.86,60,60,0,0,1,49.53,106.65,95.83,95.83,0,0,1,47.22,37.71A8,8,0,0,1,250.14,206.7Z"/></svg>}
                      </div>
                      <h3 className="text-base font-bold text-white mb-2">{v.title}</h3>
                      <p className="text-[13px] text-white/60 leading-relaxed">{v.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ── ENGAGEMENT QUALITÉ ── */}
          <section className="py-16 md:py-20" style={{ backgroundColor: '#faf9f7' }}>
            <div className="max-w-[900px] mx-auto px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: colors.primary }}>{pd.quality_kicker || 'Notre savoir-faire'}</span>
                  <h2 className="text-2xl md:text-3xl font-bold mb-5" style={{ color: '#1a1a1a' }}>{pd.quality_title || 'Engagement qualité'}</h2>
                  {pd.quality_text1 ? (
                    <p className="text-[15px] text-slate-600 leading-relaxed mb-4" style={{ whiteSpace: 'pre-line' }}>{pd.quality_text1}</p>
                  ) : (
                    <p className="text-[15px] text-slate-600 leading-relaxed mb-4">
                      Chez Magere Viandes, chaque pièce est sélectionnée avec la plus grande exigence. Nous travaillons en direct avec des <strong style={{ color: colors.primary }}>éleveurs locaux</strong> et des producteurs français reconnus.
                    </p>
                  )}
                  {pd.quality_text2 ? (
                    <p className="text-[15px] text-slate-600 leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{pd.quality_text2}</p>
                  ) : (
                    <p className="text-[15px] text-slate-600 leading-relaxed">
                      Notre bœuf est maturé sur os pendant <strong style={{ color: colors.primary }}>21 à 45 jours</strong> dans nos caves de maturation, pour développer des saveurs incomparables.
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {(pd.stats && pd.stats.length ? pd.stats : [
                    { value: '30+', label: "ans d'expérience" },
                    { value: '45j', label: 'de maturation max' },
                    { value: '35', label: 'communes livrées' },
                    { value: '100€', label: 'livraison offerte dès' },
                  ]).map((s, i) => (
                    <div key={i} className="p-4 rounded-xl text-center" style={{ backgroundColor: colors.primary + '10' }}>
                      <span className="text-3xl font-black block" style={{ color: colors.primary }}>{s.value}</span>
                      <span className="text-[12px] text-slate-600 font-medium">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ── HORAIRES & CONTACT ── */}
          {hours.length > 0 && (
            <section className="py-16 md:py-20" data-testid="hours-section">
              <div className="max-w-[900px] mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: colors.primary }}>Venez nous voir</span>
                    <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#1a1a1a' }}>Horaires d'ouverture</h2>
                    <div className="space-y-0">
                      {hours.map((h, i) => (
                        <div key={i} className="flex items-center justify-between py-3 border-b border-slate-100">
                          <span className="text-[15px] text-slate-600">{h.day}</span>
                          <span className={`text-[15px] font-semibold ${h.hours === 'Fermé' ? '' : 'text-slate-800'}`}
                            style={h.hours === 'Fermé' ? { color: colors.primary } : {}}>
                            {h.hours}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-[0.2em] mb-3 block" style={{ color: colors.primary }}>Livraison</span>
                    <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#1a1a1a' }}>A domicile</h2>
                    {deliveryInfo && (deliveryInfo.zones || []).length > 0 ? (
                      <div className="space-y-2 mb-6">
                        {deliveryInfo.zones.map((z, i) => (
                          <p key={i} className="text-[15px] text-slate-600 leading-relaxed">
                            <strong>{z.name}</strong> — {(z.delivery_days || []).join(', ')}
                            {z.free_above && <> · offerte dès <strong style={{ color: colors.primary }}>{z.free_above}€</strong></>}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[15px] text-slate-600 leading-relaxed mb-6">
                        Contactez-nous pour connaitre nos zones et jours de livraison.
                      </p>
                    )}
                    <Link to={getLink('/')}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold text-[15px] hover:brightness-110 transition-all"
                      style={{ backgroundColor: colors.primary }}
                      data-testid="cta-shop">
                      Découvrir nos produits
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          )}

        </main>
        <FooterComp />
      </div>
    );
  }

  /* ═══════════ GENERIC CMS PAGE ═══════════ */
  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" data-testid="cms-page-view" style={{ fontFamily }}>
      <HeaderComp />
      <div className="h-[50px]" />
      <main className="flex-1">
        <div className="border-b border-slate-100" style={{ backgroundColor: '#f8f9fb' }}>
          <div className="max-w-[900px] mx-auto px-4 py-3 flex items-center gap-2 text-[12px] text-slate-500">
            <Link to={getLink('/')} className="hover:underline" style={{ color: colors.secondary }}>Accueil</Link>
            <span>/</span>
            <span style={{ color: colors.primary }} className="font-medium">{page?.title}</span>
          </div>
        </div>
        <article className="max-w-[900px] mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold mb-8 pb-4 border-b" style={{ color: colors.primary, borderColor: colors.accent + '40' }}>{page?.title}</h1>
          <div className="cms-rendered-content max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(pageContent || '<p>Contenu à venir.</p>') }}
            data-testid="page-content"
          />
          <style>{`
            .cms-rendered-content h1 { font-size: 1.75rem; font-weight: 700; margin: 0.75em 0 0.5em; color: ${colors.primary}; }
            .cms-rendered-content h2 { font-size: 1.375rem; font-weight: 600; margin: 0.6em 0 0.4em; color: ${colors.primary}; }
            .cms-rendered-content h3 { font-size: 1.125rem; font-weight: 600; margin: 0.5em 0 0.3em; color: ${colors.primary}; }
            .cms-rendered-content p { margin: 0.4em 0; font-size: 0.9375rem; line-height: 1.8; color: #334155; }
            .cms-rendered-content strong { color: #0f172a; font-weight: 600; }
            .cms-rendered-content a { color: ${colors.secondary}; text-decoration: underline; font-weight: 500; }
            .cms-rendered-content ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
            .cms-rendered-content ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
            .cms-rendered-content li { margin: 0.15em 0; font-size: 0.9375rem; line-height: 1.8; color: #334155; }
            .cms-rendered-content blockquote { border-left: 3px solid #e2e8f0; padding-left: 1em; margin: 0.5em 0; color: #64748b; font-style: italic; }
            .cms-rendered-content hr { border: none; border-top: 1px solid #e2e8f0; margin: 1em 0; }
            .cms-rendered-content pre { background: #1e293b; color: #e2e8f0; border-radius: 0.5rem; padding: 0.75em 1em; font-family: monospace; font-size: 0.875rem; margin: 0.5em 0; overflow-x: auto; }
            .cms-rendered-content code { background: #f1f5f9; color: #0f172a; padding: 0.15em 0.3em; border-radius: 0.25rem; font-size: 0.875rem; }
            .cms-rendered-content img { max-width: 100%; height: auto; border-radius: 0.5rem; margin: 0.5em 0; }
            .cms-rendered-content mark { background: #fef08a; padding: 0 0.15em; }
            .cms-rendered-content [style*="text-align: center"] { text-align: center; }
            .cms-rendered-content [style*="text-align: right"] { text-align: right; }
          `}</style>
        </article>
      </main>
      <FooterComp />
    </div>
  );
};

export default CmsPageView;
